import store from '@state/store'

const requireUnauthenticated = (routeTo, routeFrom, next) => {
  // If the user is already logged in
  if (store.getters['auth/loggedIn']) {
    // Redirect to the dashboard page instead
    next({ name: 'dashboard' })
  } else {
    // Continue to the login page
    next()
  }
}

const requireNoSubscription = (routeTo, routeFrom, next) => {
  // If the user has or had a subscription all billing is handled by settings page
  if (
    store.getters['users/hasPaidSubscription'] ||
    store.getters['users/hasCanceledSubscription']
  ) {
    // Redirect to the settings billing page instead
    next({ name: 'settings:billing' })
  } else {
    // Continue to the billing page
    next()
  }
}

const requireUserDetailsIfLoggedIn = (routeTo, routeFrom, next) => {
  // If the user is already logged in get the user data
  if (store.getters['auth/loggedIn']) {
    return store.dispatch('auth/validate').then((validUser) => {
      next()
    })
  } else {
    // Continue to the requested page
    next()
  }
}

export default [
  // index.html because of strange behaviour when navigating to http://... on the live website
  {
    path: '/index.html',
    redirect: '/',
  },
  {
    path: '/',
    name: 'base',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => lazyLoadView(import('@views/dashboard.vue')),
    meta: {
      authRequired: true,
      pageHeading: 'Project Dashboard',
      chatWidgetEnabled: true,
    },
    beforeEnter(to, from, next) {
      store.dispatch('campaign/analytics/stats/clearState')
      store.dispatch('campaign/analytics/responses/clearState')
      store.dispatch('campaign/clearState')
      store.dispatch('campaign/getAllCampaigns').then(() => {
        store.dispatch('campaign/analytics/stats/getAllCampaignsStats')
      })
      store.dispatch('plans/getAvailablePlans')
      store.dispatch('plans/featureLimits/getPlanLimits')
      store.dispatch('domain/listDomains')

      next()
    },
  },
  // ================ AUTH START ================
  {
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/login.vue')),
    meta: {
      pageHeading: 'Log in',
      beforeResolve: requireUnauthenticated,
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => lazyLoadView(import('@views/register.vue')),
    meta: {
      pageHeading: 'Register',
      beforeResolve: requireUnauthenticated,
    },
    beforeEnter(to, from, next) {
      store.dispatch('signup/clearRegistrationStatus')
      next()
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut').then(() => {
          store.dispatch('campaign/clearState')
          store.dispatch('users/clearState')
          const authRequiredOnPreviousRoute = routeFrom.matched.some(
            (route) => route.meta.authRequired,
          )

          next({ name: 'login' })
          // Navigate back to previous page, or login as a fallback
          next(
            authRequiredOnPreviousRoute ? { name: 'login' } : { ...routeFrom })
        })
      },
    },
  },
  {
    path: '/account/verify-email',
    name: 'verify_email',
    component: () => lazyLoadView(import('@views/verifyEmail.vue')),
    meta: {
      authRequired: false,
      pageHeading: 'Verify Email',
    },
    // --- BUGFIX - otherwise the Navigation profile details are not being set
    beforeEnter: (to, from, next) => {
      store.dispatch('auth/validate').then(() => {
        next()
      })
    },
    // ---BUGFIX
  },
  {
    path: '/password-reset',
    name: 'password_reset',
    component: () => lazyLoadView(import('@views/passwordReset.vue')),
    meta: {
      pageHeading: 'Password Reset',
      beforeResolve: requireUnauthenticated,
    },
    beforeEnter(to, from, next) {
      store.dispatch('password/clearEmailStatus')
      next()
    },
  },
  {
    path: '/password-reset/:action/:uid/:token',
    name: 'password_reset_confirm',
    component: () => lazyLoadView(import('@views/passwordResetConfirm.vue')),
    meta: {
      pageHeading: 'Password Reset',
      beforeResolve: requireUnauthenticated,
    },
  },
  // ================ AUTH END ================

  // ================ Profile START ================

  { path: '/settings', redirect: '/settings/profile' },
  {
    path: '/settings/profile',
    name: 'settings:user',
    component: () => lazyLoadView(import('@views/profile/userDetails.vue')),
    meta: {
      authRequired: true,
      chatWidgetEnabled: true,
      isProfile: true,
    },
    beforeEnter(to, from, next) {
      store.dispatch('users/clearUpdateAccountStatus')
      next()
    },
  },
  {
    path: '/settings/password',
    name: 'password_change:user',
    component: () => lazyLoadView(import('@views/profile/passwordChange.vue')),
    meta: {
      authRequired: true,
      chatWidgetEnabled: true,
      isProfile: true,
    },
    beforeEnter(to, from, next) {
      store.dispatch('password/clearChangeStatus')
      next()
    },
  },
  {
    path: '/settings/billing',
    name: 'settings:billing',
    component: () => lazyLoadView(import('@views/profile/billingDetails.vue')),
    meta: {
      authRequired: true,
      chatWidgetEnabled: true,
      isProfile: true,
    },
    beforeEnter(to, from, next) {
      store.dispatch('subscription/clearSubscriptionStatus')
      // store.dispatch('users/invoices/getInvoices')
      store.dispatch('plans/getAvailablePlans')
      next()
    },
  },
  // ================ OLD ================
  // {
  //   path: '/profile',
  //   name: 'profile',
  //   component: () => lazyLoadView(import('@views/profile.vue')),
  //   meta: {
  //     authRequired: true,
  //     chatWidgetEnabled: true,
  //   },
  //   props: (route) => ({ user: store.state.users.currentUser || {} }),
  // },
  // {
  //   path: '/profile/:username',
  //   name: 'username-profile',
  //   component: () => lazyLoadView(import('@views/profile.vue')),
  //   meta: {
  //     authRequired: true,
  //     beforeResolve(routeTo, routeFrom, next) {
  //       store
  //       // Try to fetch the user's information by their username
  //         .dispatch('users/fetchUser', { username: routeTo.params.username })
  //         .then((user) => {
  //           // Add the user to the route params, so that it can
  //           // be provided as a prop for the view component below.
  //           routeTo.params.user = user
  //           // Continue to the route.
  //           next()
  //         })
  //         .catch(() => {
  //           // If a user with the provided username could not be
  //           // found, redirect to the 404 page.
  //           next({ name: '404', params: { resource: 'User' } })
  //         })
  //     },
  //   },
  //   // Set the user from the route params, once it's set in the
  //   // beforeResolve route guard.
  //   props: (route) => ({ user: route.params.user }),
  // },

  // ================ Profile END ================

  // ================ Base START ================
  {
    path: '/editor/:id',
    name: 'editor',
    component: () => import('@views/editor.vue'),
    meta: {
      authRequired: true,
      pageHeading: 'Editor',
      isEditor: true,
      stepIndex: 1,
    },
    beforeEnter(to, from, next) {
      store.dispatch('templates/fetchBaseTemplates')
      next()
    },
  },
  {
    path: '/setup/:id',
    name: 'setup:campaign',
    component: () => import('@views/campaignSetup.vue'),
    meta: {
      authRequired: true,
      pageHeading: 'Setup',
      isEditor: true,
      stepIndex: 3,
    },
    beforeEnter(to, from, next) {
      store.dispatch('domain/clearState')
      store.dispatch('integration/clearState')
      store.dispatch('campaign/clearState')
      store.dispatch('campaign/getCampaign', to.params.id).then(() => {
        store.dispatch('domain/listDomains')
        store.dispatch('integration/listIntegrations')
        next()
      })
    },
  },
  {
    path: '/setup-display/:id',
    name: 'setup:display',
    component: () => import('@views/campaignDisplay.vue'),
    meta: {
      authRequired: true,
      pageHeading: 'Display',
      isEditor: true,
      stepIndex: 2,
    },
    beforeEnter(to, from, next) {
      store.dispatch('campaign/getCampaign', to.params.id)
      next()
    },
  },
  {
    path: '/mailchimp/callback/',
    name: 'MailchimpOAuthCallback',
    component: () => import('@views/MailchimpOAuthCallback.vue'),
  },
  {
    path: '/aweber/callback/',
    name: 'AWeberOAuthCallback',
    component: () => import('@views/AweberOAuthCallback.vue'),
  },
  {
    path: '/brevo/callback/',
    name: 'BrevoOAuthCallback',
    component: () => import('@views/BrevoOAuthCallback.vue'),
  },
  {
    path: '/appsumo/redirect/',
    name: 'AppSumoOAuthRedirect',
    meta: {
      authRequired: false,
      pageHeading: 'License Activation',
      isAppSumo: true,
      beforeResolve: requireUnauthenticated,
    },
    component: () => import('@views/AppSumoOAuthRedirect.vue'),
  },
  {
    path: '/pixel',
    name: 'pixel',
    component: () => lazyLoadView(import('@views/pixel.vue')),
    meta: {
      authRequired: true,
      pageHeading: 'Pixel',
      chatWidgetEnabled: true,
    },
    beforeEnter(to, from, next) {
      store.dispatch('campaign/getAllCampaigns')
      store.dispatch('campaign/embedCode/checkEmbedCodePresence')
      next()
    },
  },
  {
    path: '/domains',
    name: 'domains',
    component: () => lazyLoadView(import('@views/domains.vue')),
    meta: {
      authRequired: true,
      pageHeading: 'My Domains',
      chatWidgetEnabled: true,
    },
    beforeEnter(to, from, next) {
      store.dispatch('domain/listDomains')
      store.dispatch('plans/featureLimits/getPlanLimits')
      store.dispatch('campaign/embedCode/checkEmbedCodePresence')
      next()
    },
  },
  {
    path: '/templates',
    name: 'templates',
    component: () => lazyLoadView(import('@views/templates.vue')),
    meta: {
      beforeResolve: requireUserDetailsIfLoggedIn,
      chatWidgetEnabled: false,
      pageHeading: 'Templates',
    },
    beforeEnter(to, from, next) {
      // First, ensure base widget templates are fetched from the API
      store.dispatch('templates/fetchBaseTemplates')

      if (store.getters['auth/loggedIn']) {
        store.dispatch('domain/listDomains')
        store.dispatch('plans/featureLimits/getPlanLimits')
      }
      next()
    },
  },
  {
    path: '/billing',
    name: 'billing',
    component: () => lazyLoadView(import('@views/billing.vue')),
    meta: {
      authRequired: true,
      chatWidgetEnabled: true,
      pageHeading: 'Billing',
      isBilling: true,
      beforeResolve: requireNoSubscription,
    },
    beforeEnter(to, from, next) {
      store.dispatch('plans/getAvailablePlans')
      next()
    },
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => lazyLoadView(import('@views/analytics.vue')),
    meta: {
      authRequired: true,
      chatWidgetEnabled: false,
      pageHeading: 'Analytics',
    },
    beforeEnter(to, from, next) {
      store.dispatch('campaign/analytics/stats/clearState')
      store.dispatch('campaign/analytics/responses/clearState')
      store.dispatch('campaign/getAllCampaigns').then(() => {
            // Check for a campaign parameter in the URL query
      const campaignParam = to.query.campaign

      // Build the parameters object for fetching stats/responses.
      // If a campaign parameter exists, pass it; otherwise, let your action pick the default.
      const params = {}
      if (campaignParam) {
        params.uuid = campaignParam
      }
        store.dispatch('campaign/analytics/stats/getCampaignStats', params)
        store.dispatch('campaign/analytics/responses/getCampaignResponses', params)
        next()
      })
    },
  },
  // ================ Base END ================

  {
    path: '/404',
    name: '404',
    component: require('@views/_404.vue').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // 404 page must be placed at the end !!!
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view.vue'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view.vue')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading.vue').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout.vue').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
